import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, doc, setDoc, getDoc, startAfter } from 'firebase/firestore';
import { db, auth } from '../config/firebase-config';
import { useAuthState } from 'react-firebase-hooks/auth';
import { FiStar, FiDownload, FiUsers, FiBook, FiLoader, FiAlertCircle } from 'react-icons/fi';
import './theme.css';
import FlashcardsNav from './FlashcardsNav';

const Library = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingDeck, setSavingDeck] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notification, setNotification] = useState(null);

  const DECKS_PER_PAGE = 20;

  // Show notification and auto-hide after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  useEffect(() => {
    const fetchLibraryDecks = async () => {
      try {
        setLoading(true);
        setError('');
        
        const libraryRef = collection(db, 'flashcardLibrary');
        const q = query(libraryRef, orderBy('createdAt', 'desc'), limit(DECKS_PER_PAGE));
        const querySnapshot = await getDocs(q);
        
        const decksData = [];
        querySnapshot.forEach((doc) => {
          try {
            const data = doc.data();
            if (!data.deck) {
              console.warn('Deck missing data field:', doc.id);
              return;
            }
            
            const parsedData = JSON.parse(data.deck);
            if (!parsedData || !parsedData.cards || !Array.isArray(parsedData.cards)) {
              console.warn('Invalid deck structure:', doc.id);
              return;
            }
            
            decksData.push({
              id: doc.id,
              title: parsedData.title || 'Untitled Deck',
              description: `${parsedData.subject || 'Islamic Studies'} • ${parsedData.gradeLevel || 'All Levels'}`,
              author: data.ownerID && data.ownerID !== 'anonymous' ? 'Community' : 'Islamic Studies Team',
              rating: data.rating || 0,
              downloads: data.downloads || 0,
              cards: parsedData.cards.length,
              category: parsedData.subject || 'Islamic Studies',
              featured: data.featured || false,
              createdAt: data.createdAt,
              lastEdited: data.lastEdited,
              ownerID: data.ownerID,
              tags: data.tags || [],
              public: data.public || false
            });
          } catch (parseError) {
            console.error('Error parsing deck data:', parseError, 'for deck:', doc.id);
          }
        });
        
        setDecks(decksData);
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length === DECKS_PER_PAGE);
      } catch (err) {
        console.error('Error fetching library decks:', err);
        setError('Failed to load library. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchLibraryDecks();
  }, []);

  const loadMoreDecks = async () => {
    if (!hasMore || loadingMore || !lastDoc) return;
    
    try {
      setLoadingMore(true);
      const libraryRef = collection(db, 'flashcardLibrary');
      const q = query(
        libraryRef, 
        orderBy('createdAt', 'desc'), 
        startAfter(lastDoc),
        limit(DECKS_PER_PAGE)
      );
      const querySnapshot = await getDocs(q);
      
      const newDecks = [];
      querySnapshot.forEach((doc) => {
        try {
          const data = doc.data();
          if (!data.deck) return;
          
          const parsedData = JSON.parse(data.deck);
          if (!parsedData || !parsedData.cards || !Array.isArray(parsedData.cards)) return;
          
          newDecks.push({
            id: doc.id,
            title: parsedData.title || 'Untitled Deck',
            description: `${parsedData.subject || 'Islamic Studies'} • ${parsedData.gradeLevel || 'All Levels'}`,
            author: data.ownerID && data.ownerID !== 'anonymous' ? 'Community' : 'Islamic Studies Team',
            rating: data.rating || 0,
            downloads: data.downloads || 0,
            cards: parsedData.cards.length,
            category: parsedData.subject || 'Islamic Studies',
            featured: data.featured || false,
            createdAt: data.createdAt,
            lastEdited: data.lastEdited,
            ownerID: data.ownerID,
            tags: data.tags || [],
            public: data.public || false
          });
        } catch (parseError) {
          console.error('Error parsing deck data:', parseError);
        }
      });
      
      setDecks(prev => [...prev, ...newDecks]);
      setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setHasMore(querySnapshot.docs.length === DECKS_PER_PAGE);
    } catch (err) {
      console.error('Error loading more decks:', err);
      setError('Failed to load more decks.');
    } finally {
      setLoadingMore(false);
    }
  };

  const saveDeckToUser = async (deckId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setSavingDeck(deckId);
      
      // Check if user already has this deck
      const userDeckRef = doc(db, 'users', user.uid, 'flashcards', deckId);
      const userDeckDoc = await getDoc(userDeckRef);
      
      if (userDeckDoc.exists()) {
        showNotification('You already have this deck in your collection!', 'info');
        return;
      }

      // Save to user's personal collection
      await setDoc(userDeckRef, {
        deckId: deckId,
        addedAt: new Date().toISOString(),
        isOwner: false
      });

      showNotification('Deck added to your collection!');
    } catch (err) {
      console.error('Error saving deck to user:', err);
      showNotification('Failed to add deck to your collection', 'error');
    } finally {
      setSavingDeck(null);
    }
  };

  const categories = [
    { id: 'all', name: 'All', count: decks.length },
    { id: 'Islamic Studies', name: 'Islamic Studies', count: decks.filter(d => d.category === 'Islamic Studies').length },
    { id: 'Quran', name: 'Quran', count: decks.filter(d => d.category === 'Quran').length },
    { id: 'Hadith', name: 'Hadith', count: decks.filter(d => d.category === 'Hadith').length },
    { id: 'Arabic', name: 'Arabic', count: decks.filter(d => d.category === 'Arabic').length },
  ];

  const filteredDecks = useMemo(() => {
    return decks.filter((deck) => {
      const matchesSearch = deck.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           deck.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || deck.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [decks, searchQuery, selectedCategory]);

  const featuredDecks = filteredDecks.filter(deck => deck.featured);
  const regularDecks = filteredDecks.filter(deck => !deck.featured);

  if (!user) {
    return (
      <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
        <main className="container" style={{ paddingTop: 32, paddingBottom: 32 }}>
          <FlashcardsNav />
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div className="text-foreground" style={{ fontSize: 18, marginBottom: 16 }}>
              Please sign in to access the library
            </div>
            <button className="button primary" onClick={() => navigate('/login')}>
              Sign In
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
      {/* Notification Component */}
      {notification && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '12px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '500',
            zIndex: 1000,
            background: notification.type === 'success' ? '#10b981' : 
                       notification.type === 'error' ? '#ef4444' : '#3b82f6',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          {notification.message}
        </div>
      )}

      <main className="container" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <FlashcardsNav />
        
        <div style={{ marginBottom: 24 }}>
          <h1 className="text-foreground" style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Library</h1>
          <p className="text-muted-foreground">Explore our collection of flashcard decks</p>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
            <input
              placeholder="Search decks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border rounded"
              style={{ width: '100%', padding: '10px 12px' }}
            />
          </div>
          <button className="button outline">Filter</button>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`button ${selectedCategory === category.id ? 'primary' : 'outline'}`}
              onClick={() => setSelectedCategory(category.id)}
              style={{ fontSize: 14 }}
            >
              {category.name}
              <span className="badge" style={{ marginLeft: 8 }}>{category.count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div className="text-muted-foreground" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <FiLoader className="spinner" />
              Loading library...
            </div>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div className="error-message" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <FiAlertCircle />
              {error}
            </div>
            <button 
              className="button outline" 
              onClick={() => window.location.reload()}
              style={{ marginTop: 16 }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            {featuredDecks.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h2 className="text-foreground" style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>Featured Decks</h2>
                <div className="grid" style={{ gridTemplateColumns: 'repeat(1, minmax(0, 1fr))', gap: 24 }}>
                  {featuredDecks.map((deck) => (
                    <div key={deck.id} className="card hover:shadow-card" style={{ transition: 'var(--transition-smooth)', border: 0, background: 'var(--gradient-subtle)' }}>
                      <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <div style={{ flex: 1 }}>
                            <div className="card-title" style={{ marginBottom: 4 }}>{deck.title}</div>
                            <div className="card-description">{deck.description}</div>
                          </div>
                          <span className="badge secondary">Featured</span>
                        </div>
                      </div>
                      <div className="card-content">
                        <div className="text-muted-foreground" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14 }}>
                          <span>by {deck.author}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="badge">{deck.category}</span>
                            <span className="text-muted-foreground" style={{ fontSize: 14 }}>{deck.cards} cards</span>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button 
                              className="button outline" 
                              style={{ fontSize: 14 }}
                              onClick={() => navigate(`/flashcards/${deck.id}`)}
                            >
                              Preview
                            </button>
                            <button 
                              className="button primary" 
                              style={{ fontSize: 14 }}
                              onClick={() => saveDeckToUser(deck.id)}
                              disabled={savingDeck === deck.id}
                            >
                              {savingDeck === deck.id ? 'Adding...' : 'Add to Library'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="text-foreground" style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>
                All Decks ({regularDecks.length})
              </h2>
              {regularDecks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 48 }}>
                  <div className="text-muted-foreground">No decks found matching your criteria</div>
                </div>
              ) : (
                <>
                  <div className="grid grid-3">
                    {regularDecks.map((deck) => (
                      <div key={deck.id} className="card hover:shadow-card" style={{ transition: 'var(--transition-smooth)' }}>
                        <div className="card-header">
                          <div className="card-title" style={{ fontSize: 16 }}>{deck.title}</div>
                          <div className="card-description" style={{ fontSize: 14 }}>{deck.description}</div>
                        </div>
                        <div className="card-content">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                            <span className="text-muted-foreground">by {deck.author}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span className="badge" style={{ fontSize: 12 }}>{deck.category}</span>
                              <span className="text-muted-foreground" style={{ fontSize: 12 }}>{deck.cards} cards</span>
                            </div>
                            <button 
                              className="button outline" 
                              style={{ fontSize: 14 }}
                              onClick={() => saveDeckToUser(deck.id)}
                              disabled={savingDeck === deck.id}
                            >
                              {savingDeck === deck.id ? 'Adding...' : 'Add'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Load More Button */}
                  {hasMore && (
                    <div style={{ textAlign: 'center', marginTop: 32 }}>
                      <button 
                        className="button outline"
                        onClick={loadMoreDecks}
                        disabled={loadingMore}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 auto' }}
                      >
                        {loadingMore ? (
                          <>
                            <FiLoader className="spinner" />
                            Loading more...
                          </>
                        ) : (
                          'Load More Decks'
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Library; 